import { Clock, CheckCircle2, XCircle, Loader2, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { StatusBadge, MetricCard } from '../primitives'
import { CalculationResultRenderer } from './CalculationResultRenderer'
import { ScenarioResultRenderer, isScenarioResult } from './ScenarioResultRenderer'

interface TaskStatusData {
  task_id: string
  status: string
  success?: boolean
  // Result fields (when complete)
  lca_score?: number
  score_unit?: string
  functional_unit?: {
    quantity: number
    unit: string
    activity_name?: string
  }
  activities?: {
    code: string
    name: string
    unit: string
    location: string
  }
  method?: string
  database?: string
  calculation_type?: string
  calculation_time_seconds?: number
  // Error fields
  error?: string
  message?: string
  estimated_wait_time?: number
}

interface TaskStatusRendererProps {
  data: TaskStatusData
}

type TaskState = 'pending' | 'running' | 'success' | 'error'

function getTaskState(data: TaskStatusData): TaskState {
  const status = data.status?.toUpperCase()

  if (status === 'SUCCESS' && data.success !== false) return 'success'
  if (status === 'FAILURE' || data.success === false) return 'error'
  if (status === 'STARTED' || status === 'RUNNING' || status === 'PROCESSING') return 'running'
  return 'pending'
}

export function TaskStatusRenderer({ data }: TaskStatusRendererProps) {
  const taskState = getTaskState(data)

  // If successful scenario analysis, delegate to ScenarioResultRenderer
  if (taskState === 'success' && isScenarioResult(data)) {
    return <ScenarioResultRenderer data={data} />
  }

  // If successful with LCA score, delegate to CalculationResultRenderer
  if (taskState === 'success' && data.lca_score !== undefined && data.score_unit) {
    // Type assertion is safe here because we've verified lca_score exists
    const calcData = {
      lca_score: data.lca_score,
      score_unit: data.score_unit,
      functional_unit: data.functional_unit,
      activities: data.activities,
      method: data.method,
      database: data.database,
      calculation_type: data.calculation_type,
      calculation_time_seconds: data.calculation_time_seconds,
      task_id: data.task_id,
      status: data.status,
      success: data.success,
    }
    return <CalculationResultRenderer data={calcData} />
  }

  const stateConfig = {
    pending: {
      icon: Clock,
      iconClass: 'text-muted-foreground',
      bgClass: 'bg-muted/30 border-border',
      label: 'Queued',
      description: 'Task is waiting to be processed',
    },
    running: {
      icon: Loader2,
      iconClass: 'text-info animate-spin',
      bgClass: 'bg-info-muted/50 border-info/30',
      label: 'Processing',
      description: 'Calculation in progress...',
    },
    success: {
      icon: CheckCircle2,
      iconClass: 'text-success',
      bgClass: 'bg-success-muted/50 border-success/30',
      label: 'Complete',
      description: 'Task completed successfully',
    },
    error: {
      icon: XCircle,
      iconClass: 'text-destructive',
      bgClass: 'bg-destructive/5 border-destructive/30',
      label: 'Failed',
      description: 'Task encountered an error',
    },
  }

  const config = stateConfig[taskState]
  const Icon = config.icon

  return (
    <div className="space-y-3">
      {/* Status card */}
      <div className={cn('rounded-lg border p-3', config.bgClass)}>
        <div className="flex items-center gap-3">
          <Icon className={cn('w-5 h-5', config.iconClass)} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{config.label}</span>
              <StatusBadge
                status={
                  taskState === 'running'
                    ? 'loading'
                    : taskState === 'pending'
                      ? 'pending'
                      : taskState
                }
                label={data.status}
                size="sm"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {(taskState === 'pending' || taskState === 'running') && data.message
                ? data.message
                : config.description}
            </p>
          </div>
        </div>
      </div>

      {/* Task details */}
      <div className="flex flex-wrap gap-2">
        {data.calculation_type && (
          <MetricCard label="Type" value={data.calculation_type} size="sm" />
        )}
        {data.estimated_wait_time && taskState === 'pending' && (
          <MetricCard label="Est. Wait" value={data.estimated_wait_time} unit="s" size="sm" />
        )}
      </div>

      {/* Error message */}
      {taskState === 'error' && (data.error || data.message) && (
        <div className="rounded-md bg-destructive/10 border border-destructive/30 p-2">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
            <p className="text-sm text-destructive">{data.error || data.message}</p>
          </div>
        </div>
      )}
    </div>
  )
}
